// src/runtime/LLMRuntime/types.ts
// ============================================
// LLMRuntime - 类型定义（v1.0）
//
// 职责：
//   1. 定义统一 LLM 接口（所有 Provider 实现相同接口）
//   2. 定义 LLM Provider 名称、任务、角色枚举
//   3. 定义 LLM 调用日志、路由规则、查询/命令枚举
//   4. 作为 LLMRuntime 与外部层的契约
//
// 设计原则：
//   - 模型无关：所有 Provider 实现相同 ILLMProvider 接口
//   - 自动路由：按 Role 选择最佳 Provider
//   - 失败降级：主模型不可用时自动切换到 fallback
//   - 全链路日志：token 消耗、响应时间、错误信息
// ============================================

// ============================================
// LLM Provider 名称枚举
// ============================================

/**
 * 支持的 LLM Provider 列表
 * - OpenAI: GPT 系列
 * - Claude: Anthropic Claude 系列
 * - DeepSeek: DeepSeek 系列（OpenAI 兼容）
 * - GLM: 智谱 GLM 系列（OpenAI 兼容）
 * - Qwen: 阿里通义千问（OpenAI 兼容）
 * - Gemini: Google Gemini 系列
 */
export enum LLMProviderName {
  OpenAI = 'openai',
  Claude = 'claude',
  DeepSeek = 'deepseek',
  GLM = 'glm',
  Qwen = 'qwen',
  Gemini = 'gemini',
}

// ============================================
// LLM 任务类型枚举
// ============================================

/**
 * LLM 任务类型
 * - Chat: 多轮对话
 * - Embed: 文本向量化
 * - Completion: 文本补全
 */
export enum LLMTask {
  Chat = 'chat',
  Embed = 'embed',
  Completion = 'completion',
}

// ============================================
// LLM 角色枚举（用于路由）
// ============================================

/**
 * LLM 调用角色（决定使用哪个 Provider）
 * - Writer: 内容生成 → Claude
 * - Knowledge: 知识检索 → GLM
 * - Reviewer: 内容审阅 → OpenAI (GPT-5)
 * - Planner: 任务规划 → DeepSeek
 * - Default: 默认角色
 */
export enum LLMRole {
  Writer = 'writer',
  Knowledge = 'knowledge',
  Reviewer = 'reviewer',
  Planner = 'planner',
  Default = 'default',
}

// ============================================
// LLM 查询/命令枚举
// ============================================

/**
 * LLMRuntime 查询类型
 * - Stats: 调用统计
 * - Logs: 调用日志
 * - Routes: 路由配置
 * - Providers: Provider 列表
 */
export enum LLMQuery {
  Stats = 'stats',
  Logs = 'logs',
  Routes = 'routes',
  Providers = 'providers',
}

/**
 * LLMRuntime 命令类型
 * - ConfigureRoute: 配置路由规则
 * - AddProvider: 添加 Provider
 * - RemoveProvider: 移除 Provider
 * - ClearLogs: 清空日志
 */
export enum LLMCommand {
  ConfigureRoute = 'configure_route',
  AddProvider = 'add_provider',
  RemoveProvider = 'remove_provider',
  ClearLogs = 'clear_logs',
}

// ============================================
// 聊天消息与请求
// ============================================

/**
 * 聊天消息（OpenAI 风格）
 */
export interface ChatMessage {
  /** 角色：system / user / assistant */
  role: 'system' | 'user' | 'assistant';
  /** 消息内容 */
  content: string;
}

/**
 * LLM 请求参数
 */
export interface LLMRequest {
  /** 聊天消息列表 */
  messages: ChatMessage[];
  /** 指定模型（可选，使用 Provider 默认值） */
  model?: string;
  /** 采样温度（0-2，默认 0.7） */
  temperature?: number;
  /** 最大生成 Token 数 */
  maxTokens?: number;
  /** 是否流式返回（当前统一非流式） */
  stream?: boolean;
}

// ============================================
// LLM 使用量与响应
// ============================================

/**
 * Token 使用量
 */
export interface LLMUsage {
  /** 输入 Token 数 */
  promptTokens: number;
  /** 输出 Token 数 */
  completionTokens: number;
  /** 总 Token 数 */
  totalTokens: number;
}

/**
 * LLM 响应（chat / complete 共用）
 */
export interface LLMResponse {
  /** 生成内容 */
  content: string;
  /** 实际使用的模型 */
  model: string;
  /** Token 使用量 */
  usage: LLMUsage;
  /** 响应耗时（毫秒） */
  latency: number;
  /** Provider 名称 */
  provider: LLMProviderName;
}

/**
 * Embedding 响应
 */
export interface EmbeddingResponse {
  /** 向量数据 */
  embedding: number[];
  /** 实际使用的模型 */
  model: string;
  /** Token 使用量 */
  usage: LLMUsage;
  /** 响应耗时（毫秒） */
  latency: number;
}

// ============================================
// ILLMProvider - 统一 Provider 接口
// ============================================

/**
 * LLM Provider 统一接口
 *
 * 所有 Provider（OpenAI / Claude / DeepSeek / GLM / Qwen / Gemini）必须实现此接口
 * 通过相同接口实现模型无关能力
 */
export interface ILLMProvider {
  /** Provider 名称 */
  readonly name: LLMProviderName;
  /** 多轮对话 */
  chat(req: LLMRequest): Promise<LLMResponse>;
  /** 文本向量化 */
  embed(text: string, model?: string): Promise<EmbeddingResponse>;
  /** 文本补全 */
  complete(prompt: string, options?: Partial<LLMRequest>): Promise<LLMResponse>;
  /** 是否可用（apiKey 是否存在） */
  isAvailable(): boolean;
}

// ============================================
// 调用日志与路由规则
// ============================================

/**
 * LLM 调用日志
 */
export interface LLMCallLog {
  /** 时间戳（ISO） */
  timestamp: string;
  /** Provider 名称 */
  provider: LLMProviderName;
  /** 任务类型 */
  task: LLMTask;
  /** 调用角色 */
  role: LLMRole;
  /** 实际使用的模型 */
  model: string;
  /** 响应耗时（毫秒） */
  latency: number;
  /** 是否成功 */
  success: boolean;
  /** Token 使用量（成功时） */
  usage?: LLMUsage;
  /** 错误信息（失败时） */
  error?: string;
}

/**
 * LLM 路由规则
 *
 * 主模型不可用时按 fallbacks 顺序尝试
 */
export interface LLMRouteRule {
  /** 角色 */
  role: LLMRole;
  /** 主 Provider */
  primary: LLMProviderName;
  /** 备用 Provider 列表（按优先级排序） */
  fallbacks: LLMProviderName[];
}

// ============================================
// 统一返回类型
// ============================================

/**
 * LLMRuntime 统一返回类型
 */
export interface LLMResult<T = unknown> {
  /** 是否成功 */
  success: boolean;
  /** 返回数据 */
  data?: T;
  /** 错误信息 */
  error?: string;
  /** 时间戳（ISO） */
  timestamp: string;
}

// ============================================
// Provider 配置（工厂函数使用）
// ============================================

/**
 * Provider 创建配置
 */
export interface LLMProviderConfig {
  /** API Key */
  apiKey?: string;
  /** 自定义 baseURL（可选） */
  baseURL?: string;
  /** 默认模型（可选） */
  defaultModel?: string;
}
